import { capitalize, replace } from "lodash-es";

const wordsToPreserveCapital = [
  "EC2",
  "AWS",
  "ASG",
  "S3",
  "ACM",
  "IAM",
  "DB",
  "KMS",
  "PC",
  "SES",
  "SNS",
  "SQS",
  "API",
  "GKE",
  "GCP",
];

const wordsWithSpecificMappings: Record<string, string> = {
  serverlesscontainer: "Serverless Container",
  AwsEc2NatGateway: "AWS EC2 NAT Gateway",
  AwsEc2Route: "AWS EC2 Route",
  AwsEc2VpnConnection: "AWS EC2 VPN Connection",
  AwsEc2VpnGateway: "AWS EC2 VPN Gateway",
  AwsEc2NetworkAcl: "AWS EC2 Network ACL",
};

export const convertToBeautifulString = (
  input?: string | null
): string | undefined => {
  if (input == null || input === undefined) {
    return undefined;
  }

  if (wordsToPreserveCapital.includes(input)) {
    return input;
  }

  if (wordsWithSpecificMappings[input]) {
    return wordsWithSpecificMappings[input];
  }
  // Step 1: Replace underscores and dashes with spaces
  let result = replace(input, /[_-]/g, " ");

  // Step 2: Insert spaces before capital letters (handling camelCase and PascalCase)
  result = result.replace(/([a-z])([A-Z])/g, "$1 $2");
  const finalResult = result.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");

  // Step 3: Split the string into words
  let words = finalResult.split(" ");

  // Step 4: Capitalize each word while preserving numbers and special words (s3 -> S3 and not S 3, serverlesscontainer -> Serverless Container and not Serverlesscontainer
  words = words.map((word) => {
    if (/^[0-9]+$/.test(word)) {
      return word; // Return the word as-is if it is entirely numeric
    } else if (wordsToPreserveCapital.find((x) => x === word.toUpperCase())) {
      return word.toUpperCase();
    } else if (wordsWithSpecificMappings[word]) {
      return wordsWithSpecificMappings[word];
    } else {
      return capitalize(word.toLowerCase()); // Capitalize the word
    }
  });

  // Step 5: Join the words back into a single string
  return words.join(" ");
};

export const convertToBeautifulStringOrUndefined = (
  input: string | null | undefined
): string | undefined => {
  if (input == null || input === "") {
    return undefined;
  }
  return convertToBeautifulString(input);
};

// Returns `undefined` only when `str` is `undefined` (mirrors the optional
// chaining). Overloads keep callers that pass a defined string typed as `string`.
export function trimWhiteSpaces(str: string): string;
export function trimWhiteSpaces(str?: string): string | undefined;
export function trimWhiteSpaces(str?: string): string | undefined {
  return str
    ?.split(" ")
    .filter((item) => item !== "")
    .join(" ");
}

export const getAorAN = (name: string): string => {
  return /^[aeiou]/i.test(name) ? `an ${name}` : `a ${name}`;
};

export const makeFriendlyString = (str?: string | null): string | undefined =>
  str?.replace(/_/g, " ");

export const removeSpecialCharacters = (str: string): string => {
  const regex = /[~`!@#$%^&*()|+\=?;:'",<>{}[\]\\/\s]/gi;
  return str.replace(regex, "");
};

export const isUndefinedOrNull = (value: unknown): boolean => {
  return value === undefined || value === null;
};

export const getNumberSuffix = (number: number): string => {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;

  if (lastDigit === 1 && lastTwoDigits !== 11) {
    return `${number}st`;
  }
  if (lastDigit === 2 && lastTwoDigits !== 12) {
    return `${number}nd`;
  }
  if (lastDigit === 3 && lastTwoDigits !== 13) {
    return `${number}rd`;
  }

  return `${number}th`;
};

const CSV_FORMULA_INJECTION_PATTERN = /^[\s\x00-\x1f]*[=+\-@]/;

// Returns the input unchanged when falsy, so an `undefined` input yields
// `undefined`. Overloads keep callers that pass a defined string typed as `string`.
export function sanitizeCsvCell(value: string): string;
export function sanitizeCsvCell(value?: string): string | undefined;
export function sanitizeCsvCell(value?: string): string | undefined {
  if (!value?.length) {
    return value;
  }

  if (CSV_FORMULA_INJECTION_PATTERN.test(value)) {
    return `'${value}`;
  }
  return value;
}

export const trimUnicodeSpaces = (str?: string | null): string | undefined =>
  str?.replace(/[\s ]+/g, "");

const VALID_ID_FORMAT = /^[^:]+:-?\d+$/;

export const getValidBase64 = (input?: string | null): string | null => {
  const trimmed = trimUnicodeSpaces(input);
  if (!trimmed) return null;
  try {
    const padded = trimmed.padEnd(Math.ceil(trimmed.length / 4) * 4, "=");
    atob(padded);
    return padded;
  } catch {
    return null;
  }
};

export const getValidId = (paramsId?: string | null): string | null => {
  const padded = getValidBase64(paramsId);
  if (!padded) return null;
  try {
    if (!VALID_ID_FORMAT.test(atob(padded))) {
      return null;
    }
    return padded;
  } catch {
    return null;
  }
};
